import _kebabCase from 'lodash/kebabCase'
import snakeCase from 'lodash/snakeCase'

import { FLOWCODE_PIXEL_ID_QUERY_PARAM } from '@components/flowcode/pixel-id-query-param'
import { HOSTING_GUIDE_QUERY_PARAM_KEY } from '@components/hosting-guide/query-param-key'
import { PHOTO_GUIDE_QUERY_PARAM_KEY } from '@components/onboarding/photo-guide/query-param-key'
import { ListingType, ManageGuestsFilter } from '@libs/graphql/types'
import {
  HostLandingPaths,
  LandingPageListingType,
} from '@page/BecomeAHostTemplate/types'
import { HomepageListingType } from '@page/home/home.types'
import { HostManagementFormIds } from '@page/host-management/forms/forms.types'
import { ASK_DAISY_PRICING_QUERY_PARAM_KEY } from '@page/host-onboarding/constants/ask-daisy-query-params'
import { STARTING_SCREEN_V2 } from '@page/host-onboarding/constants/starting-screens.v2.constants'
import { HomeListingPaths } from '@page/listing-type-home/home.types'
import {
  MenuOptionNames,
  ReservationViewNames,
} from '@page/my-reservations/types'
import { MANAGE_GUESTS_FILTER_QUERY_PARAM } from '@page/pool-management/screens/pro-tab/manage-guests/filter-query-param'
import { PRO_TAB_QUERY_PARAM_KEY } from '@page/pool-management/screens/pro-tab/tab-query-param'
import {
  isProTabTab,
  ProTabTab,
} from '@page/pool-management/screens/pro-tab/types'
import { ManageItemId } from '@page/pool-management/v2/types'
import { URLSearchFiltersKeys } from '@page/search/search-filter-keys.types'
import { AppSessionStorage } from '@state/storage-wrapper'
import { slugify } from '@utils/strings'

function constructRoute<
  QP extends Record<string, string> = Record<string, string>
>(parts: Array<string | undefined>, queryParams?: QP) {
  const route = `/${parts
    .flatMap((part) => (part ? slugify(part) : []))
    .join('/')}`

  if (!queryParams) return route

  const filteredKeys = Object.keys(queryParams).filter(
    (key) => !!queryParams[key]
  )

  if (filteredKeys.length === 0) return route

  const filteredParams = filteredKeys.reduce(
    (acc, key) => ({
      ...acc,
      [key]: queryParams[key],
    }),
    {} as QP
  )
  const urlSearchParams = new URLSearchParams(filteredParams)

  return route.concat(`?${urlSearchParams.toString()}`)
}

/** Preserves comma-separated multi-select; snake-cases each slug only. */
function searchUseCaseQueryValue(useCase: string): string {
  return useCase
    .split(',')
    .map((part) => snakeCase(part.trim()))
    .filter(Boolean)
    .join(',')
}

const landingSpaceTypes: Record<
  LandingPageListingType | HomepageListingType,
  HostLandingPaths | HomeListingPaths
> = {
  [ListingType.tennisCourt]: 'tennis',
  [ListingType.basketballCourt]: 'hoops',
  [ListingType.home]: 'homes',
  [ListingType.pickleball]: 'pickleball',
  [ListingType.pool]: 'pools',
  [ListingType.largeBackyard]: 'homes',
  [ListingType.igloo]: '',
}

export type SpecialOfferScreen = 'create' | 'view' | 'edit'

// Any screen or form that uses the listingProperties query and dynamic form component should be added here
export type HostSpaceSettingsScreen =
  // V1 Space Settings Screens
  | 'space-details'
  | 'space-features'
  | 'restroom'
  | 'privacy'
  | 'parking'

  // V2 Space Settings Screens
  | 'pool-type'
  | 'restroom_v2'
  | 'privacy_v2'
  | 'guest-parking'
  | 'basic'
  | 'special'
  | 'rules'
  | 'guest-count' // MAYBE
  | 'guest-rules' // MAYBE
  | 'off-peak'
  | 'availability'
  | 'preferences'
  | 'instant-book' // MAYBE
  | 'cancellation-policy'

export type HostManagementFormIdsCombined =
  | HostManagementFormIds
  | HostSpaceSettingsScreen

type HostingOnboardingStepsV2 =
  // Phase 1
  | 'listing-type'
  | 'listing-categories'
  | 'pool-type'
  | 'basics-preview'
  | 'where'
  | 'finalize-location'
  | 'basics'
  | 'special'
  | 'restroom'
  | 'privacy'
  | 'rules'

  // Phase 2
  | 'brand-preview'
  | 'photos'
  | 'daisy-branding' // only for loading state
  | 'title'
  | 'description'

  // Phase 3
  | 'bookings-preview'
  | 'guest-count'
  | 'guest-rules'
  | 'daisy-pricing' // only for loading state
  | 'base-pricing'
  | 'pricing'
  | 'off-peak'
  | 'availability'
  | 'preferences'
  | 'instant-book'
  | 'cancellation-policy'

  // Phase 4 / Guest check in
  | 'complete'
  | 'live'
  | 'guest-parking'
  | 'guest-check-in'
  | 'guest-access'
  | 'welcome-kit'

  // Save and exit
  | 'ready-to-continue'

  // Select from multiple incomplete listings
  | 'listing-selection'

export type HostingOnboardingSteps =
  | HostingOnboardingStepsV2
  | 'getting-started'
  | 'location'
  | 'details'
  | 'guests'
  | 'next-steps'
  | 'photos'
  | 'pricing'
  | 'space-type'
  | 'start-date'
  | 'title'
  | 'how-swimply-works'
  | 'verification'

export const routes = {
  Home: (
    params: {
      listingType?: HomepageListingType
    } = {}
  ) =>
    constructRoute([
      `/${params.listingType ? landingSpaceTypes[params.listingType] : ''}`,
    ]),
  Search: (queryParams?: Partial<Record<URLSearchFiltersKeys, string>>) => {
    return constructRoute(['/s'], {
      ...queryParams,
      space_type: queryParams?.space_type
        ? snakeCase(queryParams?.space_type)
        : '',
      use_case: queryParams?.use_case
        ? searchUseCaseQueryValue(queryParams.use_case)
        : '',
    })
  },
  Favorites: (params?: { collectionId?: string }) =>
    constructRoute(['/favorites', params?.collectionId]),
  ForYou: () => constructRoute(['/for-you']),

  ChatRules: () => constructRoute(['/chat-rules']),
  SendAnInquiry: (params: { listingUserId: string }) =>
    constructRoute(['/inquiry', params.listingUserId]),
  ViewInquiry: (params: { listingUserId: string }) =>
    constructRoute(['/inquiry', params.listingUserId, 'view']),
  Messages: (queryParams?: { otherPartyId?: string }) =>
    constructRoute(['/messages'], queryParams),

  Listing: (
    params: {
      listingId: string
      bookingId?: string
      stealth?: boolean
      preview?: boolean
    },
    queryParams?: {
      searchId?: string
      // set to true if we want to do the shortened flow if things are selected
      short?: string
      // set to true if we want to display the joined waitlist modal
      joined?: string
      [FLOWCODE_PIXEL_ID_QUERY_PARAM]?: string
    }
  ) => {
    const flowcodePixelId =
      queryParams?.[FLOWCODE_PIXEL_ID_QUERY_PARAM] ??
      AppSessionStorage.getItem(FLOWCODE_PIXEL_ID_QUERY_PARAM)
    return constructRoute(
      [
        '/pooldetails',
        `${params.stealth ? 's' : ''}${params.listingId}`,
        params.bookingId,
        params.preview ? 'preview' : '',
      ],
      {
        ...queryParams,
        [FLOWCODE_PIXEL_ID_QUERY_PARAM]: flowcodePixelId ?? '',
      }
    )
  },
  ListingReviews: (params: { listingId: string }) =>
    constructRoute(['/listing', params.listingId, 'reviews']),

  Booking: (
    params: { bookingId: string },
    queryParams?: {
      showReviewModal?: string
      // This has to match with the reservation id in order to default as true
      showReviewRequestModal?: string
      openIniateRefundModal?: string
      openRequestRefundModal?: string
    }
  ) => ({
    details: constructRoute(
      ['/reservation-details', params.bookingId],
      queryParams
    ),
    manage: constructRoute(
      ['/reservation-details', params.bookingId, 'manage'],
      queryParams
    ),
    pricing: constructRoute(
      ['/reservation-details', params.bookingId, 'pricing'],
      queryParams
    ),
  }),

  Reservations: (params: {
    menuOption: MenuOptionNames
    userOption: ReservationViewNames
  }) =>
    constructRoute(['/reservations', params?.menuOption, params?.userOption]),

  Checkout: (params: {
    listingId: string
    bookingId?: string
    partnership?: string
    lfl?: string | null
  }) => ({
    amenities: constructRoute(
      ['/checkout', params.listingId, 'amenities', params.bookingId],
      { lfl: params.lfl ?? '' }
    ),
    messageHost: constructRoute(
      ['/checkout', params.listingId, 'message-host', params.bookingId],
      { lfl: params.lfl ?? '' }
    ),
    memberships: constructRoute(
      ['/checkout', params.listingId, 'memberships', params.bookingId],
      { lfl: params.lfl ?? '' }
    ),
    confirm: constructRoute(
      ['/checkout', params.listingId, 'confirm', params.bookingId],
      { partnership: params.partnership ?? '', lfl: params.lfl ?? '' }
    ),
    setup: constructRoute(
      ['/checkout', params.listingId, 'setup', params.bookingId],
      { lfl: params.lfl ?? '' }
    ),
  }),

  Profile: (
    params: {
      section?: 'edit-profile' | 'edit-account' | 'guest-verification'
      openPremium?: string
    } = {}
  ) =>
    constructRoute(['/profile', params.section], {
      openPremium: params?.openPremium ?? '',
    }),

  HostManagement: (
    params: {
      listingId?: string
      screenV2?: ManageItemId
      tab?:
        | 'welcome'
        | 'welcome-back'
        | 'performance'
        | 'marketing'
        | 'host-settings'
        | 'space-settings'
        | 'memberships'
        | ProTabTab
      formId?: HostManagementFormIdsCombined
      fromWelcomeTab?: string
      showFans?: string
      showOffseason?: string
      isV2?: boolean
      includeSecurityDeposit?: boolean
      photoGuide?: boolean
      askDaisyPricing?: boolean
      hostingGuide?: boolean
      returnToScreenName?: ManageItemId
      manageGuestsFilter?: ManageGuestsFilter
    } = {}
  ) => {
    // Early return for v2 screens
    if (params.screenV2 && params.listingId) {
      const proTabTab = isProTabTab(params.tab) ? params.tab : ''
      return constructRoute(['/host', params.listingId, params.screenV2], {
        returnToScreenName: params.returnToScreenName ?? '',
        [PHOTO_GUIDE_QUERY_PARAM_KEY]: params.photoGuide ? 'true' : '',
        [ASK_DAISY_PRICING_QUERY_PARAM_KEY]: params.askDaisyPricing
          ? 'true'
          : '',
        [HOSTING_GUIDE_QUERY_PARAM_KEY]: params.hostingGuide ? 'true' : '',
        [PRO_TAB_QUERY_PARAM_KEY]: proTabTab,
        [MANAGE_GUESTS_FILTER_QUERY_PARAM]: params.manageGuestsFilter ?? '',
      })
    }

    const {
      isV2,
      includeSecurityDeposit,
      returnToScreenName,
      photoGuide,
      askDaisyPricing,
      hostingGuide,
      manageGuestsFilter,
      ...rest
    } = params

    return constructRoute(['/host'], {
      ...rest,
      listingId: params.listingId ?? '',
      tab: params.tab ?? '',
      fromWelcomeTab: params.fromWelcomeTab ?? '',
      showFans: params.showFans ?? '',
      showOffseason: params.showOffseason ?? '',
      [HOSTING_GUIDE_QUERY_PARAM_KEY]: params.hostingGuide ? 'true' : '',
      [MANAGE_GUESTS_FILTER_QUERY_PARAM]: params.manageGuestsFilter ?? '',
    })
  },

  ProTab: () => constructRoute(['/host-pro']),

  EarningsTab: () => constructRoute(['/host-earnings']),

  Onboarding: (params: {
    onboardingV2: boolean
    listingId?: string
    step?: HostingOnboardingSteps
    queryParams?: Record<string, string>
    base?: boolean
  }) => {
    if (params.base) {
      return constructRoute(['/hosts', 'onboarding'])
    }
    if (params.onboardingV2) {
      if (!params.listingId) {
        const stepsWithoutListingId: HostingOnboardingSteps[] = [
          'pool-type',
          'listing-type',
          'listing-categories',
          'listing-selection',
        ]
        if (params.step && stepsWithoutListingId.includes(params.step)) {
          return constructRoute(
            ['/hosts', 'onboarding', 'v2', params.step],
            params.queryParams
          )
        }
        return constructRoute(
          ['/hosts', 'onboarding', 'v2', STARTING_SCREEN_V2],
          params.queryParams
        )
      }

      if (!params.step) {
        return constructRoute(
          ['/hosts', 'onboarding', 'v2', params.listingId, 'ready-to-continue'],
          params.queryParams
        )
      }

      return constructRoute(
        ['/hosts', 'onboarding', 'v2', params.listingId, params.step],
        params.queryParams
      )
    }

    if (!params.listingId && !params.step) {
      return constructRoute(
        ['/hosts', 'onboarding', 'getting-started'],
        params.queryParams
      )
    }

    if (params.step === 'location' || params.step === 'getting-started') {
      return constructRoute(
        ['/hosts', 'onboarding', params.step],
        params.queryParams
      )
    }

    return constructRoute(
      ['/hosts', 'onboarding', params.listingId, params.step],
      params.queryParams
    )
  },

  Earnings: (
    params: {
      listingId?: string
      isHostManagementV2?: boolean
    } & (
      | {
          tab?: 'insights'
          transactionsTab?: ''
        }
      | {
          tab?: 'transactions'
          transactionsTab?: 'completed' | 'upcoming'
        }
    ) = {}
  ) => {
    if (params.isHostManagementV2) {
      return constructRoute([
        '/host',
        params.listingId,
        'earnings',
        params.tab,
        params.transactionsTab,
      ])
    }
    return constructRoute([
      '/host',
      'earnings',
      params.listingId,
      params.tab,
      params.transactionsTab,
    ])
  },

  BusinessKit: (params: { listingId?: string } = {}) =>
    constructRoute(['/host', params.listingId, 'business-kit']),

  Calendar: (params: { listingId?: string } = {}) =>
    constructRoute(['/calendar', params.listingId]),

  BecomeAHost: (params: { listingType?: LandingPageListingType } = {}) => {
    if (params.listingType === 'pool') {
      return constructRoute(['/become-a-host'])
    }

    return constructRoute([
      '/become-a-host',
      params.listingType ? landingSpaceTypes[params.listingType] : undefined,
    ])
  },

  Invite: () => constructRoute(['/invite']),
  Invitees: () => constructRoute(['/invitees']),

  ReferralClaimSuccess: () => constructRoute(['/referral-claim-success']),

  NotFound: () => constructRoute(['/notfound']),

  TermsOfService: () => constructRoute(['/termsandconditions']),
  PrivacyPolicy: () => constructRoute(['/privacy']),

  ReservationSuccess: (params: { bookingId: string; listingId: string }) =>
    constructRoute([
      '/reservation-success',
      'listing',
      params.listingId,
      'booking',
      params.bookingId,
    ]),

  Explore: (
    params: {
      location?: string
      spaceTypeUseCase?: string
      filter?: string
    } = {}
  ) =>
    constructRoute(
      ['/explore', params?.location, params?.spaceTypeUseCase],
      params.filter ? { filter: params.filter } : undefined
    ),

  SpecialOffers: (params: { listingUserId?: string; bookingId?: string }) => ({
    create: constructRoute(['/special-offers', params.listingUserId]),
    view: constructRoute(['/special-offers', params.listingUserId, 'view']),
    edit: constructRoute(['/special-offers', params.bookingId, 'edit']),
  }),

  NotificationSettings: () => constructRoute(['/notification-settings']),
  SmsDownload: () => constructRoute(['/sms-download']),
  MembershipsOverview: () => constructRoute(['/membership-overview']),
  DownloadApp: () => constructRoute(['/app-download']),
  InstagramAccountDeletionConfirmation: (params: {
    confirmationCode: string
  }) =>
    constructRoute([
      '/instagram',
      'data-deletion',
      'confirmation',
      params?.confirmationCode,
    ]),
  PublicPools: (params?: { state?: string; city?: string }) =>
    constructRoute([
      '/pools-in',
      params?.state ? _kebabCase(params?.state?.toLowerCase()) : undefined,
      params?.state && params?.city
        ? _kebabCase(params?.city?.toLowerCase())
        : undefined,
    ]),
  AddLifeguard: (params: { bookingId: string }) =>
    constructRoute(['/add-lifeguard', params.bookingId]),
  SummerAwards: (params?: { citySlug?: string }) => {
    const basePath = '/choice-awards-2025'
    if (params?.citySlug) {
      return constructRoute([basePath, params.citySlug])
    }
    return constructRoute([basePath])
  },
}

export type RouteResults<RouteName extends keyof typeof routes> = NonNullable<
  ReturnType<(typeof routes)[RouteName]>
>

export type RouteParams<RouteName extends keyof typeof routes> = NonNullable<
  Parameters<(typeof routes)[RouteName]>[0]
>

export type RouteQueryParams<RouteName extends keyof typeof routes> =
  NonNullable<Parameters<(typeof routes)[RouteName]>[1]>
